import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";
import { contactFaq, siteData } from "./siteData";

const howWeHelp = [
  {
    title: "Submit a Project",
    description: "Have a project idea or technical challenge? Let's discuss how our team can help bring your vision to life.",
    link: "/contact",
    linkLabel: "Learn More →",
  },
  {
    title: "Join Our Team",
    description: "Looking to start or advance your engineering career? Explore opportunities for recent graduates and professionals.",
    link: "/careers",
    linkLabel: "View Opportunities →",
  },
  {
    title: "Become a Mentor",
    description: "Experienced professional? Share your knowledge and help develop the next generation of engineering talent.",
    link: "/contact",
    linkLabel: "Get Involved →",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Project Inquiry", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus("sent");
        setForm({ name: "", email: "", phone: "", subject: "Project Inquiry", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <SiteLayout title="Contact" description="Contact SaskTech Lab about projects, careers, or mentorship.">
      <PageHero
        eyebrow="Contact Us"
        title="Let's discuss how we can work together to bring your ideas to life."
        description="Whether you're looking to discuss a potential project, explore career opportunities, or learn more about our mentorship program, we'd love to hear from you. Our team will get back to you within 1–2 business days."
      />

      {/* Contact details + form */}
      <section className="site-shell section-space">
        <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="panel-card h-fit">
            <SectionHeader eyebrow="Get In Touch" title="Contact details" description="" />
            <div className="mt-8 space-y-5 text-sm leading-7 text-slate-600">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1">Visit Us</p>
                <p>{siteData.addressLines.join(", ")}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1">Email Us</p>
                <a href={`mailto:${siteData.email}`} className="hover:text-emerald-700 transition-colors">{siteData.email}</a>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1">Call Us</p>
                <a href={siteData.phoneHref} className="hover:text-emerald-700 transition-colors">{siteData.phone}</a>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1">Office Hours</p>
                <p>Mon–Fri: 9:00 AM – 5:00 PM CST</p>
                <p>Weekends: Closed</p>
              </div>
            </div>
          </div>

          <div className="panel-card">
            <SectionHeader eyebrow="Send A Message" title="Start with a simple inquiry" description="" />
            {status === "sent" ? (
              <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-200 p-8 text-center">
                <p className="font-display text-xl font-bold text-emerald-800 mb-2">Message sent!</p>
                <p className="text-sm text-slate-600">Thank you — we'll get back to you within 1–2 business days. A confirmation has been sent to your email.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 grid gap-4">
                <input name="name" value={form.name} onChange={handleChange} placeholder="Your Name" className="field-input" required />
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email Address" className="field-input" required />
                <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Phone Number (Optional)" className="field-input" />
                <select name="subject" value={form.subject} onChange={handleChange} className="field-select">
                  <option>Project Inquiry</option>
                  <option>Career Opportunities</option>
                  <option>Mentorship</option>
                  <option>Other</option>
                </select>
                <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your project, question, or request..." rows="6" className="field-textarea" required />
                {status === "error" && (
                  <p className="text-sm text-red-600">Failed to send message. Please try again or email us directly at <a href={`mailto:${siteData.email}`} className="underline">{siteData.email}</a>.</p>
                )}
                <button type="submit" disabled={status === "sending"} className="button-primary w-full sm:w-fit disabled:opacity-60">
                  {status === "sending" ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* How Can We Help */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="How Can We Help You?" title="Three ways to work with us." description="Choose the path that best matches your needs." />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {howWeHelp.map((item) => (
            <article key={item.title} className="panel-card h-full flex flex-col">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{item.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600 flex-1">{item.description}</p>
              <div className="mt-6">
                <Link to={item.link} className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
                  {item.linkLabel}
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="site-shell section-space pb-10">
        <SectionHeader eyebrow="FAQ" title="Frequently asked questions." description="A few direct answers to common questions about how we work." />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {contactFaq.map((item) => (
            <article key={item.question} className="panel-card h-full">
              <h3 className="font-display text-xl font-bold text-emerald-950">{item.question}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{item.answer}</p>
            </article>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}